import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import {
  Alert,
  Avatar,
  Box,
  Chip,
  Fab,
  IconButton,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import PsychologyAltIcon from "@mui/icons-material/PsychologyAlt";
import RefreshIcon from "@mui/icons-material/Refresh";

import { useAuth } from "src/contexts/auth";
import { useExecutiveGate } from "src/hooks/useExecutiveGate";
import { useKateChatStore } from "src/ai/aiChatStore";
import { dispatchTool } from "src/ai/tools";
import { useSkillsStore } from "src/ai/skills/skillsStore";
import { useMemoryStore } from "src/ai/memories/memoryStore";
import { MessageList } from "./MessageList";
import { StatusHistory } from "./StatusHistory";
import { SessionsSidebar } from "./SessionsSidebar";
import { MemoriesDialog } from "./MemoriesDialog";
import { ACTIVE_MODEL } from "src/ai/modelInfo";
import kateAvatar from "src/assets/kate.jpg";

declare global {
  interface Window {
    __telemetryAuth?: {
      getAccessTokenSilently: () => Promise<string>;
    };
  }
}

const PANEL_WIDTH = 480;
const PANEL_HEIGHT = 640;

export const KateChatPanel: React.FC = () => {
  const isExec = useExecutiveGate();
  const { user, getAccessTokenSilently } = useAuth();
  const history = useHistory();
  const location = useLocation();

  const isOpen = useKateChatStore((s) => s.isOpen);
  const isLoading = useKateChatStore((s) => s.isLoading);
  const isSidebarOpen = useKateChatStore((s) => s.isSidebarOpen);
  const messages = useKateChatStore((s) => s.messages);
  const statusHistory = useKateChatStore((s) => s.statusHistory);
  const error = useKateChatStore((s) => s.error);
  const setOpen = useKateChatStore((s) => s.setOpen);
  const toggleSidebar = useKateChatStore((s) => s.toggleSidebar);
  const sendMessage = useKateChatStore((s) => s.sendMessage);
  const setContext = useKateChatStore((s) => s.setContext);
  const clearError = useKateChatStore((s) => s.clearError);
  const clearActiveSession = useKateChatStore((s) => s.clearActiveSession);
  const activeSessionId = useKateChatStore((s) => s.activeSessionId);
  const createSession = useKateChatStore((s) => s.createSession);

  const [draft, setDraft] = useState("");
  const [isMaximized, setIsMaximized] = useState(false);
  const [memoriesOpen, setMemoriesOpen] = useState(false);

  const loadSkills = useSkillsStore((s) => s.loadSkills);
  const refreshSkills = useSkillsStore((s) => s.refresh);
  const skillsLoadedAt = useSkillsStore((s) => s.loadedAt);
  const memoryCount = useMemoryStore((s) => s.memories.length);

  // Expose the auth token getter to the tool layer (which has no React access).
  useEffect(() => {
    if (getAccessTokenSilently) {
      window.__telemetryAuth = { getAccessTokenSilently };
    }
    return () => {
      if (window.__telemetryAuth?.getAccessTokenSilently === getAccessTokenSilently) {
        window.__telemetryAuth = undefined;
      }
    };
  }, [getAccessTokenSilently]);

  // Keep TelemetryContext (system prompt section) up to date.
  useEffect(() => {
    setContext({
      userName: (user as { name?: string } | undefined)?.name,
      userEmail: (user as { email?: string } | undefined)?.email,
      currentPath: location.pathname,
    });
  }, [user, location.pathname, setContext]);

  // Listen for navigate_to tool calls and route the user.
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ path: string }>).detail;
      if (detail?.path) history.push(detail.path);
    };
    window.addEventListener("kate-navigate", handler);
    return () => window.removeEventListener("kate-navigate", handler);
  }, [history]);

  // Make sure there's always at least one session once the panel opens.
  useEffect(() => {
    if (isOpen && !activeSessionId) {
      createSession();
    }
  }, [isOpen, activeSessionId, createSession]);

  // Load skills the first time the panel is opened (so we don't pay the
  // network round-trip for non-executive sessions). The skills store
  // self-deduplicates concurrent calls.
  useEffect(() => {
    if (isOpen && isExec && skillsLoadedAt === null) {
      void loadSkills();
    }
  }, [isOpen, isExec, skillsLoadedAt, loadSkills]);

  const handleSend = useCallback(async () => {
    const text = draft.trim();
    if (!text || isLoading) return;
    setDraft("");
    await sendMessage(text, dispatchTool, getAccessTokenSilently);
  }, [draft, isLoading, sendMessage, getAccessTokenSilently]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        void handleSend();
      }
    },
    [handleSend],
  );

  const fabBackground = useMemo(
    () =>
      "linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #ec4899 100%)",
    [],
  );

  if (!isExec) return null;

  return (
    <>
      {!isOpen && (
        <Tooltip title="Ask Kate" placement="left">
          <Fab
            onClick={() => setOpen(true)}
            aria-label="Open Kate"
            sx={{
              position: "fixed",
              bottom: 24,
              right: 24,
              zIndex: 2000,
              width: 39,
              height: 39,
              minHeight: 39,
              background: fabBackground,
              color: "white",
              padding: 0,
              overflow: "hidden",
              border: "2px solid rgba(255,255,255,0.7)",
              transition:
                "box-shadow 0.25s ease, transform 0.25s ease, filter 0.25s ease",
              "&:hover": {
                filter: "brightness(1.08)",
                transform: "scale(1.06)",
                boxShadow:
                  "0 0 0 4px rgba(236,72,153,0.25), 0 0 24px 6px rgba(139,92,246,0.55), 0 0 40px 10px rgba(99,102,241,0.35)",
              },
            }}
          >
            <Avatar
              src={kateAvatar}
              alt="Kate"
              sx={{ width: "100%", height: "100%" }}
            />
          </Fab>
        </Tooltip>
      )}

      {isOpen && (
        <Paper
          elevation={8}
          sx={{
            position: "fixed",
            bottom: isMaximized ? 16 : 24,
            right: isMaximized ? 16 : 24,
            width: isMaximized
              ? "calc(100vw - 32px)"
              : PANEL_WIDTH + (isSidebarOpen ? 200 : 0),
            height: isMaximized ? "calc(100vh - 32px)" : PANEL_HEIGHT,
            zIndex: 2000,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            borderRadius: 2,
            transition:
              "width 0.18s ease, height 0.18s ease, bottom 0.18s ease, right 0.18s ease",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 1.5,
              py: 1,
              background: fabBackground,
              color: "white",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Tooltip title={isSidebarOpen ? "Hide sessions" : "Show sessions"}>
                <IconButton
                  size="small"
                  onClick={toggleSidebar}
                  sx={{ color: "white" }}
                >
                  {isSidebarOpen ? <ChevronLeftIcon /> : <ChevronRightIcon />}
                </IconButton>
              </Tooltip>
              <Avatar
                src={kateAvatar}
                alt="Kate"
                sx={{
                  width: 28,
                  height: 28,
                  border: "1.5px solid rgba(255,255,255,0.7)",
                }}
              />
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Kate
              </Typography>
              <Tooltip title={`${ACTIVE_MODEL.family} ${ACTIVE_MODEL.label} • ${ACTIVE_MODEL.id}`}>
                <Chip
                  label={ACTIVE_MODEL.label}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: "0.68rem",
                    fontWeight: 600,
                    letterSpacing: "0.02em",
                    color: "white",
                    backgroundColor: "rgba(255,255,255,0.18)",
                    border: "1px solid rgba(255,255,255,0.3)",
                    "& .MuiChip-label": { px: 0.85 },
                  }}
                />
              </Tooltip>
            </Box>
            <Box>
              <Tooltip title={`Memories (${memoryCount})`}>
                <IconButton
                  size="small"
                  onClick={() => setMemoriesOpen(true)}
                  sx={{ color: "white" }}
                >
                  <PsychologyAltIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Refresh skills from database">
                <IconButton
                  size="small"
                  onClick={() => void refreshSkills()}
                  sx={{ color: "white" }}
                >
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Clear this conversation">
                <IconButton
                  size="small"
                  onClick={clearActiveSession}
                  sx={{ color: "white" }}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={isMaximized ? "Restore" : "Maximize"}>
                <IconButton
                  size="small"
                  onClick={() => setIsMaximized((v) => !v)}
                  sx={{ color: "white" }}
                >
                  {isMaximized ? (
                    <FullscreenExitIcon fontSize="small" />
                  ) : (
                    <FullscreenIcon fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>
              <Tooltip title="Close">
                <IconButton
                  size="small"
                  onClick={() => setOpen(false)}
                  sx={{ color: "white" }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          <Box sx={{ flex: 1, display: "flex", overflow: "hidden" }}>
            {isSidebarOpen && <SessionsSidebar />}
            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                minWidth: 0,
              }}
            >
              <MessageList messages={messages} isLoading={isLoading} />
              <StatusHistory history={statusHistory} isLoading={isLoading} />
              {error && (
                <Alert
                  severity="error"
                  onClose={clearError}
                  sx={{ borderRadius: 0 }}
                >
                  {error}
                </Alert>
              )}
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  p: 1.25,
                  borderTop: "1px solid rgba(0,0,0,0.08)",
                  alignItems: "flex-end",
                }}
              >
                <TextField
                  fullWidth
                  multiline
                  maxRows={5}
                  size="small"
                  placeholder="Ask Kate about runs..."
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={handleKeyDown}
                  disabled={isLoading}
                  sx={{
                    "& .MuiInputBase-root": { fontSize: "0.9rem" },
                  }}
                />
                <Tooltip title="Send">
                  <span>
                    <IconButton
                      onClick={handleSend}
                      disabled={isLoading || draft.trim().length === 0}
                      color="primary"
                    >
                      <SendIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            </Box>
          </Box>
        </Paper>
      )}

      <MemoriesDialog open={memoriesOpen} onClose={() => setMemoriesOpen(false)} />
    </>
  );
};
